import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import type { TowerDriverDetail, TowerShipment } from "../../../types/controlTower";
import TowerShipmentsMap from "../map/TowerShipmentsMap";
import { findLatestDeliveredShipment } from "../model/latestDelivery";
import { buildDriverRoute } from "../model/driverRoute";
import { searchKey } from "../model/towerFormat";
import type { DriverFocus } from "../model/driverFocus";
import { activateOnKey, hasCoordinates } from "../model/towerShared";
import DriverExtras from "./DriverExtras";
import DriverRouteTable from "./DriverRouteTable";
import DriverShipmentTable from "./DriverShipmentTable";

// Estado de los envíos que se ven (mapa y lista a la vez). "route" es "Todos"
// con las visitas de hoy numeradas y unidas en el orden en que se registraron.
type StatusView = "inTransit" | "notDelivered" | "delivered" | "all" | "route";

const STATUS_VIEWS: [StatusView, string][] = [
  ["inTransit", "En camino"],
  ["notDelivered", "No entregados"],
  ["delivered", "Entregados"],
  ["all", "Todos"],
  ["route", "Todos + recorrido"],
];

function matchesStatus(s: TowerShipment, view: StatusView) {
  if (view === "all" || view === "route") return true;
  if (view === "inTransit") return s.status === "inTransit";
  if (view === "delivered") return s.status === "delivered";
  return s.status !== "delivered" && s.status !== "cancelled";
}

// Detalle de un chofer: tarjetas que filtran, mapa y lista con el mismo filtro.
// initialMapStatus / initialStatus: con qué estado abre (desde Ubicar choferes,
// "all"). initialZone e initialUniverse: el recorte del contador que se tocó.
// focus: el aviso de la alerta desde la que se abrió.
export default function TowerDriverDialog({
  driver,
  date,
  operationalDateLabel,
  countLabel,
  onClose,
  initialMapStatus,
  initialStatus,
  initialZone,
  initialUniverse,
  focus,
}: {
  driver: TowerDriverDetail;
  date: string;
  operationalDateLabel: string;
  countLabel: string;
  onClose: () => void;
  initialMapStatus?: "inTransit" | "notDelivered" | "all";
  initialStatus?: string;
  initialZone?: string;
  initialUniverse?: "FLEX" | "NON_FLEX";
  focus?: DriverFocus;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const mapSectionRef = useRef<HTMLElement>(null);
  const [search, setSearch] = useState("");
  // Las tarjetas muestran lo de hoy (driver.counts) y, aparte, "+N" de lo que
  // arrastra de días anteriores (driver.carried). En el mapa y la lista se ven
  // juntos, los de días anteriores marcados.
  const { counts, carried } = driver;
  // Recorte del contador desde el que se abrió (zona y tipo); se puede quitar.
  const [narrow, setNarrow] = useState<{ zone?: string; universe?: "FLEX" | "NON_FLEX" }>({
    zone: initialZone,
    universe: initialUniverse,
  });
  const inScope = (s: TowerShipment) =>
    (!narrow.zone || s.zone === narrow.zone) && (!narrow.universe || s.shippingUniverse === narrow.universe);
  // Sin nada en camino en lo que se abre, arranca en "Todos".
  const openedInTransit = driver.shipments.filter((s) => inScope(s) && s.status === "inTransit").length;
  const requested = initialMapStatus || (initialStatus === "inTransit" ? "inTransit" : undefined);
  const [view, setView] = useState<StatusView>(requested || (openedInTransit ? "inTransit" : "all"));
  const routeMode = view === "route";
  // "Ver en mapa": el envío al que el mapa tiene que ir (seq fuerza repetirlo).
  const [mapFocus, setMapFocus] = useState<{ id: string; seq: number } | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  const narrowLabel = [
    narrow.universe === "FLEX" ? "Flex" : narrow.universe === "NON_FLEX" ? "Particulares" : null,
    narrow.zone && driver.zones.length > 1 ? `Zona ${narrow.zone}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const query = searchKey(search);
  const scoped = driver.shipments.filter(
    (s) => inScope(s) && (!query || searchKey(`${s.tracking} ${s.client} ${s.address} ${s.locality}`).includes(query)),
  );
  // El mismo filtro para el mapa y la lista.
  // "Todos + recorrido" es el día completo: la lista también, sin recortes ni búsqueda.
  const rows = view === "route" ? driver.shipments : scoped.filter((s) => matchesStatus(s, view));
  const mappedRows = rows.filter(hasCoordinates);
  const lastDelivery = useMemo(() => findLatestDeliveredShipment(driver.shipments), [driver.shipments]);
  const hasLastDeliveryCoordinates = Boolean(lastDelivery && hasCoordinates(lastDelivery));
  // El recorrido es el del día completo: no lo recortan los filtros.
  const route = useMemo(() => buildDriverRoute(driver.shipments, date), [driver.shipments, date]);
  // "Todos + recorrido" es el día completo, como el recorrido: los envíos del
  // mapa y el "por visitar" tampoco los recortan la búsqueda ni la zona o el tipo.
  // Lo que el mapa del recorrido dibuja además de las paradas: lo que queda por
  // visitar (en camino o pendiente) con ubicación.
  const routeShipments = useMemo(
    () => driver.shipments.filter((s) => hasCoordinates(s) && (s.status === "inTransit" || s.status === "pending")),
    [driver.shipments],
  );
  const toVisit = useMemo(() => {
    const visited = new Set(route.stops.map((stop) => stop.shipment.id));
    return driver.shipments.filter((s) => (s.status === "inTransit" || s.status === "pending") && !visited.has(s.id))
      .length;
  }, [driver.shipments, route.stops]);
  const routeLocated = route.stops.filter(
    (stop) => stop.shipment.latitude !== null && stop.shipment.longitude !== null,
  ).length;

  // "+N" de una tarjeta: filtra por ese estado (y tipo); los de días
  // anteriores siguen a la vista, marcados con ↺.
  const previousNote = (count: number, onlyInTransit: boolean, universe?: "FLEX" | "NON_FLEX") =>
    count > 0 && (
      <button
        type="button"
        className="tower-previous-note"
        onKeyDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          setNarrow({ universe });
          setView(onlyInTransit ? "inTransit" : "all");
        }}
      >
        +{count} de días anteriores
      </button>
    );
  // Tarjeta que filtra lo de hoy por estado (y tipo, en Flex y Particulares).
  const kpiCard = (
    label: string,
    value: number,
    valueClass: string,
    cardView: StatusView,
    universe?: "FLEX" | "NON_FLEX",
    note?: React.ReactNode,
  ) => {
    const active = view === cardView && narrow.universe === universe && !narrow.zone;
    const apply = () => {
      setView(cardView);
      setNarrow({ universe });
    };
    return (
      <div
        className={`tower-kpi-filter${active ? " active" : ""}`}
        role="button"
        tabIndex={0}
        aria-pressed={active}
        title={`Ver en el mapa y la lista: ${label.toLocaleLowerCase("es")}`}
        onClick={apply}
        onKeyDown={activateOnKey(apply)}
      >
        <small>{label}</small>
        <strong className={valueClass || undefined}>{value}</strong>
        {note}
      </div>
    );
  };
  const showOnMap = (id: string) => {
    if (routeMode) setView("all");
    setMapFocus((previous) => ({ id, seq: (previous?.seq || 0) + 1 }));
    mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <dialog
      ref={ref}
      className="tower-dialog"
      aria-labelledby="tower-driver-title"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="tower-dialog-head">
        <div>
          <span className="tower-eyebrow">
            {driver.zones.map((zone) => (zone === "Sin clasificar" ? "Zona por revisar" : zone)).join(" / ")}
          </span>
          <div className="tower-dialog-title">
            <h2 id="tower-driver-title">{driver.name}</h2>
            <span className="tower-dialog-last" title="Última entrega registrada del chofer hoy.">
              Última entrega <b>{driver.lastDelivery ? driver.lastDelivery.slice(11) || "sin hora" : "—"}</b>
            </span>
          </div>
        </div>
        <Button variant="secondary" onClick={onClose}>
          Cerrar detalle ×
        </Button>
      </div>
      {focus && (
        <div className={`tower-dialog-focus ${focus.tone}`} role="status">
          <strong>{focus.title}</strong>
          <span>{focus.detail}</span>
        </div>
      )}
      <DriverExtras counts={driver.counts} />
      {/* Las tarjetas también filtran el mapa y la lista; la activa queda resaltada. */}
      <div className="tower-dialog-kpis">
        {kpiCard(`${countLabel} hoy`, counts.total, "", "all", undefined, previousNote(carried.total, false))}
        {kpiCard("Entregados", counts.delivered, "tower-green", "delivered")}
        {kpiCard(
          "En camino total",
          counts.inTransit,
          "",
          "inTransit",
          undefined,
          previousNote(carried.inTransit, true),
        )}
        {kpiCard(
          "Flex en camino",
          counts.flexInTransit,
          "tower-gold",
          "inTransit",
          "FLEX",
          previousNote(carried.flexInTransit, true, "FLEX"),
        )}
        {kpiCard(
          "Particulares en camino",
          counts.nonFlexInTransit,
          "tower-violet",
          "inTransit",
          "NON_FLEX",
          previousNote(carried.nonFlexInTransit, true, "NON_FLEX"),
        )}
      </div>

      {/* Un solo juego de filtros para el mapa y la lista: estado (y el recorte, si hay). */}
      <div className="tower-dialog-filters">
        <div className="tower-scope-tabs">
          {narrowLabel && (
            <button
              type="button"
              className="tower-scope-narrow"
              onClick={() => setNarrow({})}
              title="Mostrar todos los tipos y zonas"
            >
              Solo {narrowLabel} ×
            </button>
          )}
        </div>
        <div className="tower-map-filters">
          <div className="tower-map-filter-group" role="group" aria-label="Estado">
            {STATUS_VIEWS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`tower-map-filter${view === value ? " active" : ""}`}
                aria-pressed={view === value}
                onClick={() => {
                  // El recorrido es del día completo: se quitan el recorte y la búsqueda.
                  if (value === "route") {
                    setNarrow({});
                    setSearch("");
                  }
                  setView(value);
                }}
                title={
                  value === "route"
                    ? "Todos los envíos, con las visitas de hoy numeradas en el orden en que se registraron."
                    : undefined
                }
              >
                {label}
                {value === "route" ? "" : ` ${scoped.filter((s) => matchesStatus(s, value)).length}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section ref={mapSectionRef} className="tower-map-section" aria-label="Ubicación de los envíos">
        <div className="tower-map-heading">
          <strong>{routeMode ? "Recorrido del día" : "Mapa de envíos"}</strong>
          {routeMode ? (
            <span>
              {route.stops.length} visitas en orden de registro · {route.stops.length - routeLocated} sin coordenadas
              {route.withoutTime ? ` · ${route.withoutTime} sin hora` : ""}
              {toVisit > 0 ? ` · ${toVisit} por visitar` : ""}
            </span>
          ) : (
            <span>
              Mostrando <b>{rows.length}</b>{" "}
              {STATUS_VIEWS.find(([value]) => value === view)?.[1].toLocaleLowerCase("es")}
              {rows.length - mappedRows.length > 0 && (
                <em className="tower-map-missing">
                  {" "}
                  · {rows.length - mappedRows.length} sin ubicación (están en la lista, no en el mapa)
                </em>
              )}
              {scoped.length > rows.length && (
                <>
                  {" "}
                  · {scoped.length - rows.length} en otros estados{" "}
                  <button type="button" className="tower-show-on-map" onClick={() => setView("all")}>
                    Ver todos
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        {routeMode ? (
          routeLocated || routeShipments.length ? (
            <TowerShipmentsMap shipments={routeShipments} route={route.stops} />
          ) : (
            <div className="tower-map-placeholder tower-map-empty">
              {route.stops.length
                ? "Las visitas de hoy no tienen coordenadas."
                : "Todavía no hay visitas registradas hoy."}
            </div>
          )
        ) : mappedRows.length || hasLastDeliveryCoordinates ? (
          <TowerShipmentsMap shipments={mappedRows} lastDelivery={lastDelivery} focus={mapFocus} />
        ) : (
          <div className="tower-map-placeholder tower-map-empty">No hay envíos con coordenadas en este filtro.</div>
        )}
        {routeMode && route.stops.length > 0 && <DriverRouteTable stops={route.stops} />}
      </section>

      <div className="tower-toolbar">
        <div className="tower-search">
          <Input
            label="Buscar envío"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={routeMode}
            placeholder={
              routeMode
                ? "El recorrido muestra el día completo: elegí otro filtro para buscar"
                : "Tracking, cliente, dirección o localidad"
            }
          />
        </div>
      </div>
      <div className="tower-dialog-count">
        <strong>
          {rows.length} {rows.length === 1 ? "envío" : "envíos"}
        </strong>
        <span>
          {STATUS_VIEWS.find(([value]) => value === view)?.[1]} · hoy y días anteriores
          {narrowLabel ? ` · Solo ${narrowLabel}` : ""}
        </span>
      </div>
      <DriverShipmentTable shipments={rows} operationalDateLabel={operationalDateLabel} onShowOnMap={showOnMap} />
    </dialog>
  );
}
